import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { marcoApi } from './api';
import { ASSETS } from '../../config/assets';
import { MARCO_PAGES } from './config';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, FileText, Calendar, User, Lock, Copy, Check, Trash2, Download
} from 'lucide-react';

const MarcoView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [marco, setMarco] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const t = (en, fr) => language === 'fr' ? fr : en;

  useEffect(() => {
    const load = async () => {
      try {
        const data = await marcoApi.get(id);
        setMarco(data);
      } catch {
        navigate('/marcom');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate]);

  const copyPassword = () => {
    navigator.clipboard.writeText(marco.password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const API = '';
      const res = await fetch(`${API}/api/marcom/${id}/pdf?lang=${language}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${marco.name}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    await marcoApi.delete(id);
    navigate('/marcom');
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-wine border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!marco) return null;

  return (
    <div data-testid="marco-view" className="min-h-screen bg-[#0D0D0D]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0D0D0D]/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/marcom')}
              className="p-2 text-white/50 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <span className="font-heading text-lg font-semibold text-white truncate">
              {marco.name}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {confirmDelete ? (
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white text-xs rounded-lg" data-testid="confirm-delete-btn">
                  {t('Confirm Delete', 'Confirmer')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)} className="text-white/50 text-xs">
                  {t('Cancel', 'Annuler')}
                </Button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                data-testid="delete-btn"
                className="p-2 text-white/30 hover:text-red-400 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Meta info */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-2 text-white/40 mb-1">
              <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-wider">{t('Pages', 'Pages')}</span>
            </div>
            <span className="text-white text-xl font-heading font-semibold">{marco.page_count}</span>
          </div>
          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-2 text-white/40 mb-1">
              <User className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-wider">{t('Author', 'Auteur')}</span>
            </div>
            <span className="text-white text-sm font-medium">{marco.created_by_name}</span>
          </div>
          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-2 text-white/40 mb-1">
              <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-wider">{t('Created', 'Cree')}</span>
            </div>
            <span className="text-white text-sm font-medium">{formatDate(marco.created_at)}</span>
          </div>
          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-2 text-white/40 mb-1">
              <Lock className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-wider">{t('Password', 'Mot de passe')}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-white text-sm font-mono">{marco.password}</span>
              <button onClick={copyPassword} className="text-white/30 hover:text-wine transition-colors">
                {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />}
              </button>
            </div>
          </div>
        </div>

        {/* Presentation Preview */}
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-white/30 mb-4">
          {t('Presentation Preview', 'Apercu de la Presentation')}
        </h3>
        <div className="bg-[#111] border border-white/10 rounded-xl overflow-hidden">
          <div className="p-6 space-y-4">
            {marco.pages.map((page, idx) => {
              const catalogPage = MARCO_PAGES.find(p => p.id === page.page_id);
              return (
                <div
                  key={page.page_id}
                  data-testid={`preview-page-${idx}`}
                  className="relative bg-[#1A1A1A] border border-white/5 rounded-lg p-6 overflow-hidden"
                >
                  {/* Watermark */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.04]">
                    <img src={ASSETS.LOGO} alt="" className="w-28 h-auto" />
                  </div>
                  <div className="relative z-10">
                    <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-wine/60">
                      {t('Page', 'Page')} {idx + 1} / {marco.page_count}
                    </span>
                    <h4 className="font-heading text-xl font-medium text-white mt-1">
                      {language === 'fr' ? page.title_fr : page.title_en}
                    </h4>
                    {catalogPage && (
                      <p className="text-white/40 text-sm mt-2">
                        {language === 'fr' ? catalogPage.descFr : catalogPage.descEn}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="border-t border-white/5 px-6 py-3 flex items-center justify-between">
            <span className="text-xs text-white/30">{marco.page_count} {t('pages', 'pages')}</span>
            <span className="text-xs text-white/20">Marco by YUL On Tap</span>
          </div>
        </div>

        {/* Download Section */}
        <div className="mt-6 bg-[#1A1A1A] border border-white/10 rounded-xl p-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-white font-heading font-medium text-base">
                {t('Download PDF', 'Telecharger le PDF')}
              </h3>
              <p className="text-white/40 text-sm mt-1">
                {t(
                  'Password-protected PDF with professional watermarking.',
                  'PDF protege par mot de passe avec filigrane professionnel.'
                )}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <Lock className="w-3.5 h-3.5 text-wine" strokeWidth={1.5} />
                <span className="text-xs text-white/50">
                  {t('PDF Password:', 'Mot de passe PDF:')}
                </span>
                <code className="text-xs text-white font-mono bg-white/5 px-2 py-0.5 rounded">{marco.password}</code>
              </div>
            </div>
            <Button
              onClick={handleDownload}
              disabled={downloading}
              data-testid="download-pdf-btn"
              className="bg-wine hover:bg-wine-light text-white rounded-xl px-6 h-11 text-sm font-medium flex-shrink-0"
            >
              {downloading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t('Generating...', 'Generation...')}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Download className="w-4 h-4" strokeWidth={1.5} />
                  {t('Download PDF', 'Telecharger PDF')}
                </span>
              )}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MarcoView;
